import { IBillingAdapter } from './IBillingAdapter';
import { ApiResponse } from '../../types/api';
import {
  WorkOrderCost,
  Quotation,
  Bill,
  CreateCostDto,
  CreateQuotationDto,
  CreateBillDto,
  CostFilter,
  QuotationFilter,
  BillFilter,
  CostLineItem,
} from '../../types/billing';
import { hasApiEndpoint } from '../../config/backend';

const MOCK_COSTS: WorkOrderCost[] = [
  {
    cost_id: 'CST-2026-00001',
    work_order_id: 'WO-2026-00046',
    complaint_id: 'CMP-2026-00102',
    vendor_id: 'VND-002',
    materials: [
      { item_id: 'CLI-001', description: 'PVC Elbow Joint 2-inch', quantity: 2, unit: 'pcs', unit_price: 120, amount: 240, category: 'material' },
      { item_id: 'CLI-002', description: 'Seal Ring', quantity: 2, unit: 'pcs', unit_price: 45, amount: 90, category: 'material' },
      { item_id: 'CLI-003', description: 'PVC Pipe 2-inch (1m)', quantity: 1, unit: 'mtr', unit_price: 180, amount: 180, category: 'material' },
    ],
    labor: [
      { item_id: 'CLI-004', description: 'Plumbing Labor (2 hrs)', quantity: 2, unit: 'hrs', unit_price: 250, amount: 500, category: 'labor' },
    ],
    service: [
      { item_id: 'CLI-005', description: 'Travel & Visit Charge', quantity: 1, unit: 'lot', unit_price: 150, amount: 150, category: 'service' },
    ],
    other: [],
    materials_subtotal: 510,
    labor_subtotal: 500,
    service_subtotal: 150,
    other_subtotal: 0,
    subtotal: 1160,
    tax_rate: 18,
    tax_amount: 208.80,
    total: 1368.80,
    currency: 'Rs',
    status: 'approved',
    created_at: '2026-09-13T11:00:00Z',
    updated_at: '2026-09-13T11:30:00Z',
  },
];

const MOCK_QUOTATIONS: Quotation[] = [
  {
    quotation_id: 'QUO-2026-00001',
    work_order_id: 'WO-2026-00001',
    vendor_id: 'VND-001',
    vendor_name: 'CoolTech Services',
    items: [
      { item_id: 'QIT-001', description: 'Compressor (1.5 Ton)', quantity: 1, unit: 'pcs', unit_price: 8500, amount: 8500, category: 'material' },
      { item_id: 'QIT-002', description: 'Refrigerant Gas R410A', quantity: 2, unit: 'kg', unit_price: 600, amount: 1200, category: 'material' },
      { item_id: 'QIT-003', description: 'AC Filter', quantity: 2, unit: 'pcs', unit_price: 400, amount: 800, category: 'material' },
      { item_id: 'QIT-004', description: 'Technician Service (4 hrs)', quantity: 4, unit: 'hrs', unit_price: 500, amount: 2000, category: 'labor' },
      { item_id: 'QIT-005', description: 'Travel Charge', quantity: 1, unit: 'lot', unit_price: 1000, amount: 1000, category: 'service' },
    ],
    subtotal: 13500,
    tax_amount: 2430,
    total: 15930,
    validity_days: 7,
    notes: 'Standard AC repair service including parts and labor. Warranty 30 days on compressor.',
    status: 'submitted',
    submitted_at: '2026-09-10T10:30:00Z',
  },
  {
    quotation_id: 'QUO-2026-00002',
    work_order_id: 'WO-2026-00002',
    vendor_id: 'VND-003',
    vendor_name: 'Alpha Electrical Services',
    items: [
      { item_id: 'QIT-006', description: 'MCB Circuit Breaker 32A', quantity: 2, unit: 'pcs', unit_price: 850, amount: 1700, category: 'material' },
      { item_id: 'QIT-007', description: 'Wiring Cable 2.5 sqmm', quantity: 10, unit: 'mtr', unit_price: 65, amount: 650, category: 'material' },
      { item_id: 'QIT-008', description: 'Electrical Labor (3 hrs)', quantity: 3, unit: 'hrs', unit_price: 600, amount: 1800, category: 'labor' },
    ],
    subtotal: 4150,
    tax_amount: 747,
    total: 4897,
    validity_days: 14,
    notes: 'Panel board MCB replacement and rewiring for Production line 3.',
    status: 'approved',
    submitted_at: '2026-09-11T08:00:00Z',
    approved_at: '2026-09-11T14:00:00Z',
    approved_by: 'Rajesh Kumar',
  },
];

const MOCK_BILLS: Bill[] = [
  {
    bill_id: 'BIL-2026-00001',
    work_order_id: 'WO-2026-00002',
    complaint_id: 'CMP-2026-00101',
    vendor_id: 'VND-003',
    vendor_name: 'Alpha Electrical Services',
    quotation_id: 'QUO-2026-00002',
    line_items: [
      { item_id: 'BLI-001', description: 'MCB Circuit Breaker 32A', quantity: 2, unit: 'pcs', unit_price: 850, amount: 1700, category: 'material' },
      { item_id: 'BLI-002', description: 'Wiring Cable 2.5 sqmm', quantity: 10, unit: 'mtr', unit_price: 65, amount: 650, category: 'material' },
      { item_id: 'BLI-003', description: 'Electrical Labor (3 hrs)', quantity: 3, unit: 'hrs', unit_price: 600, amount: 1800, category: 'labor' },
    ],
    subtotal: 4150,
    tax_amount: 747,
    total: 4897,
    bill_date: '2026-09-12',
    due_date: '2026-09-26',
    payment_status: 'pending',
  },
];

let costCounter = MOCK_COSTS.length;
let quotationCounter = MOCK_QUOTATIONS.length;
let billCounter = MOCK_BILLS.length;

export class GASBillingAdapter implements IBillingAdapter {
  private costs: WorkOrderCost[] = [...MOCK_COSTS];
  private quotations: Quotation[] = [...MOCK_QUOTATIONS];
  private bills: Bill[] = [...MOCK_BILLS];

  async getCostByWorkOrder(workOrderId: string): Promise<ApiResponse<WorkOrderCost | null>> {
    if (!hasApiEndpoint()) {
      const cost = this.costs.find(c => c.work_order_id === workOrderId) || null;
      return { success: true, data: cost, message: cost ? 'Cost loaded' : 'No cost entry found' };
    }
    return { success: false, data: null, message: 'API not configured' };
  }

  async createOrUpdateCost(data: CreateCostDto): Promise<ApiResponse<WorkOrderCost>> {
    if (!hasApiEndpoint()) {
      const materialsSubtotal = data.materials.reduce((sum, i) => sum + i.amount, 0);
      const laborSubtotal = data.labor.reduce((sum, i) => sum + i.amount, 0);
      const serviceSubtotal = data.service.reduce((sum, i) => sum + i.amount, 0);
      const otherSubtotal = data.other.reduce((sum, i) => sum + i.amount, 0);
      const subtotal = materialsSubtotal + laborSubtotal + serviceSubtotal + otherSubtotal;
      const taxAmount = subtotal * (data.tax_rate / 100);

      const existingIndex = this.costs.findIndex(c => c.work_order_id === data.work_order_id);
      const costData: WorkOrderCost = {
        cost_id: existingIndex >= 0 ? this.costs[existingIndex].cost_id : `CST-2026-${String(++costCounter).padStart(5, '0')}`,
        work_order_id: data.work_order_id,
        complaint_id: data.complaint_id,
        vendor_id: data.vendor_id,
        materials: data.materials,
        labor: data.labor,
        service: data.service,
        other: data.other,
        materials_subtotal: materialsSubtotal,
        labor_subtotal: laborSubtotal,
        service_subtotal: serviceSubtotal,
        other_subtotal: otherSubtotal,
        subtotal,
        tax_rate: data.tax_rate,
        tax_amount: taxAmount,
        total: subtotal + taxAmount,
        currency: 'Rs',
        status: 'draft',
        created_at: existingIndex >= 0 ? this.costs[existingIndex].created_at : new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (existingIndex >= 0) {
        this.costs[existingIndex] = costData;
      } else {
        this.costs.push(costData);
      }
      return { success: true, data: costData, message: 'Cost entry saved' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async deleteCostLineItem(costId: string, itemId: string): Promise<ApiResponse<WorkOrderCost>> {
    if (!hasApiEndpoint()) {
      const cost = this.costs.find(c => c.cost_id === costId);
      if (!cost) return { success: false, data: null as any, message: 'Cost entry not found' };

      const removeItem = (items: CostLineItem[]) => items.filter(i => i.item_id !== itemId);
      cost.materials = removeItem(cost.materials);
      cost.labor = removeItem(cost.labor);
      cost.service = removeItem(cost.service);
      cost.other = removeItem(cost.other);

      cost.materials_subtotal = cost.materials.reduce((s, i) => s + i.amount, 0);
      cost.labor_subtotal = cost.labor.reduce((s, i) => s + i.amount, 0);
      cost.service_subtotal = cost.service.reduce((s, i) => s + i.amount, 0);
      cost.other_subtotal = cost.other.reduce((s, i) => s + i.amount, 0);
      cost.subtotal = cost.materials_subtotal + cost.labor_subtotal + cost.service_subtotal + cost.other_subtotal;
      cost.tax_amount = cost.subtotal * (cost.tax_rate / 100);
      cost.total = cost.subtotal + cost.tax_amount;
      cost.updated_at = new Date().toISOString();

      return { success: true, data: cost, message: 'Line item removed' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async getQuotations(filter?: QuotationFilter): Promise<ApiResponse<Quotation[]>> {
    if (!hasApiEndpoint()) {
      let data = [...this.quotations];
      if (filter?.work_order_id) data = data.filter(q => q.work_order_id === filter.work_order_id);
      if (filter?.vendor_id) data = data.filter(q => q.vendor_id === filter.vendor_id);
      if (filter?.status) data = data.filter(q => q.status === filter.status);
      return { success: true, data, message: 'Quotations loaded' };
    }
    return { success: false, data: [], message: 'API not configured' };
  }

  async getQuotationById(id: string): Promise<ApiResponse<Quotation>> {
    if (!hasApiEndpoint()) {
      const q = this.quotations.find(q => q.quotation_id === id);
      if (q) return { success: true, data: q, message: 'Quotation loaded' };
      return { success: false, data: null as any, message: 'Quotation not found' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async submitQuotation(data: CreateQuotationDto): Promise<ApiResponse<Quotation>> {
    if (!hasApiEndpoint()) {
      const subtotal = data.items.reduce((sum, i) => sum + i.amount, 0);
      const taxAmount = subtotal * 0.18;
      const newQuotation: Quotation = {
        quotation_id: `QUO-2026-${String(++quotationCounter).padStart(5, '0')}`,
        work_order_id: data.work_order_id,
        vendor_id: data.vendor_id,
        vendor_name: 'Current Vendor',
        items: data.items,
        subtotal,
        tax_amount: taxAmount,
        total: subtotal + taxAmount,
        validity_days: data.validity_days,
        notes: data.notes,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
      };
      this.quotations.unshift(newQuotation);
      return { success: true, data: newQuotation, message: 'Quotation submitted' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async approveQuotation(id: string, approvedBy: string): Promise<ApiResponse<Quotation>> {
    if (!hasApiEndpoint()) {
      const q = this.quotations.find(q => q.quotation_id === id);
      if (!q) return { success: false, data: null as any, message: 'Quotation not found' };
      q.status = 'approved';
      q.approved_at = new Date().toISOString();
      q.approved_by = approvedBy;
      return { success: true, data: q, message: 'Quotation approved' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async rejectQuotation(id: string, reason: string): Promise<ApiResponse<Quotation>> {
    if (!hasApiEndpoint()) {
      const q = this.quotations.find(q => q.quotation_id === id);
      if (!q) return { success: false, data: null as any, message: 'Quotation not found' };
      q.status = 'rejected';
      q.rejection_reason = reason;
      return { success: true, data: q, message: 'Quotation rejected' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async getBills(filter?: BillFilter): Promise<ApiResponse<Bill[]>> {
    if (!hasApiEndpoint()) {
      let data = [...this.bills];
      if (filter?.work_order_id) data = data.filter(b => b.work_order_id === filter.work_order_id);
      if (filter?.vendor_id) data = data.filter(b => b.vendor_id === filter.vendor_id);
      if (filter?.payment_status) data = data.filter(b => b.payment_status === filter.payment_status);
      return { success: true, data, message: 'Bills loaded' };
    }
    return { success: false, data: [], message: 'API not configured' };
  }

  async getBillById(id: string): Promise<ApiResponse<Bill>> {
    if (!hasApiEndpoint()) {
      const b = this.bills.find(b => b.bill_id === id);
      if (b) return { success: true, data: b, message: 'Bill loaded' };
      return { success: false, data: null as any, message: 'Bill not found' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async generateBill(data: CreateBillDto): Promise<ApiResponse<Bill>> {
    if (!hasApiEndpoint()) {
      const subtotal = data.line_items.reduce((sum, i) => sum + i.amount, 0);
      const taxAmount = subtotal * (data.tax_rate / 100);
      const newBill: Bill = {
        bill_id: `BIL-2026-${String(++billCounter).padStart(5, '0')}`,
        work_order_id: data.work_order_id,
        complaint_id: data.complaint_id,
        vendor_id: data.vendor_id,
        vendor_name: 'Current Vendor',
        quotation_id: data.quotation_id,
        line_items: data.line_items,
        subtotal,
        tax_amount: taxAmount,
        total: subtotal + taxAmount,
        bill_date: new Date().toISOString().split('T')[0],
        due_date: data.due_date,
        payment_status: 'pending',
      };
      this.bills.unshift(newBill);
      return { success: true, data: newBill, message: 'Bill generated' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async markBillPaid(billId: string, paymentReference: string): Promise<ApiResponse<Bill>> {
    if (!hasApiEndpoint()) {
      const b = this.bills.find(b => b.bill_id === billId);
      if (!b) return { success: false, data: null as any, message: 'Bill not found' };
      b.payment_status = 'paid';
      b.payment_date = new Date().toISOString().split('T')[0];
      b.payment_reference = paymentReference;
      return { success: true, data: b, message: 'Bill marked as paid' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }
}
